import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button } from 'react-bootstrap5';
import {
  Link, useParams, useLocation, useNavigate,
} from 'react-router-dom';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';

import routes from 'app/routes';
import usePlan from 'components/Common/PolicyPlans/Hooks/usePlan';
import HorizontalSelectButtonsField from 'components/new_ui/Form/HorizontalSelectButtonsField';
import HorizontalNumberField from 'components/new_ui/Form/HorizontalNumberField';
import HorizontalDateField from 'components/new_ui/Form/HorizontalDateField';
import HorizontalTextField from 'components/new_ui/Form/HorizontalTextField';
import HorizontalSelectField from 'components/new_ui/Form/HorizontalSelectField';
import formatDate from 'components/utils/formatDate';
import formatCurrency from 'components/utils/formatCurrency';
import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';
import Total from 'components/Managers/Policy/Plans/Widgets/Total';
import usePaymentMethods from 'components/Common/Billing/Hooks/usePaymentMethods';
import PlanDetailsHeader from './PlanDetailsHeader';

const pricyByCurrency = (abbr) => {
  if (abbr === 'GBP' || abbr === 'CHF') {
    return 100;
  } if (abbr === 'USD') {
    return 100;
  }
  return 135;
};

export default function PlanDetails({ enterpriseId }) {
  const { state } = useLocation();
  const { planId } = useParams();

  const plan = state?.plan || usePlan(enterpriseId, planId).plan;

  const mark = plan ? plan.attributes.currency.mark : '';
  const abbr = plan ? plan.attributes.currency.title : '';

  const paymentMethods = usePaymentMethods(enterpriseId);

  const navigate = useNavigate();

  const isFree = plan?.attributes?.is_free || false;

  if (plan === null) {
    return (
      <>
        <PlanDetailsHeader />

        <section>
          <LoadingPlaceholder text="Loading plan..." />
        </section>
      </>
    );
  }

  const validationSchema = {
    name: Yup.string().required('Plan Name is required'),
    sessions: Yup.number().required('Full Name is required'),
    membersCovered: Yup.number().required('Number of members covered is required')
      .min(1, 'Number of members covered must be greater than 0'),
    billingType: Yup.string().required('Type of billing is required'),
    effectiveDate: Yup.string().required('Effective Date is required'),
  };

  if (!isFree) {
    validationSchema.paymentInformationId = Yup.string().nullable().required('Payment Method is required');
  }

  if (plan && plan.attributes.rules) {
    plan.attributes.rules.forEach((rule) => {
      if (rule.type === 'employee_minimal') {
        validationSchema.membersCovered = Yup.number()
          .required('Number of members covered is required')
          .min(rule.value, `Number of members covered must be equal to ${rule.value} or greater`);
      }
    });
  }

  const formSchema = Yup.object().shape(validationSchema);

  const onSubmitHundler = (values) => {
    const newPlan = {
      ...plan,
      attributes: {
        ...plan.attributes,
        name: values.name,
        effective_date: formatDate(values.effectiveDate),
        effective_date_form: formatDate(values.effectiveDate),
        employees_covered: values.membersCovered,
        payment_mode: values.billingType,
        renewal_date_form: values.renewalDate,
        monthly_total: values.total,
        payment_information_id: values.paymentInformationId,
        payment_information_label: values.paymentInformationId ? paymentMethods
          .find((item) => item.id === values.paymentInformationId)
          .attributes.label : '',
      },
    };

    navigate(
      isFree ? routes.managers.plans.edit.summary(planId) : routes.managers.plans.edit.services(planId),
      {
        state: {
          plan: newPlan,
        },
      },
    );
  };

  const initialValues = {
    name: plan.attributes.name,
    sessions: plan.attributes.sessions,
    membersCovered: plan.attributes.employees_covered,
    billingType: plan.attributes.payment_mode,
    total: plan.attributes.monthly_total,
    effectiveDate: new Date(plan?.attributes?.effective_date_form),
    renewalDate: plan?.attributes?.renewal_date_form,
    paymentInformationId: plan?.attributes?.payment_information_id,
  };

  return (
    <>
      <section className="border-bottom border-light">
        <h1 className="mb-3">Plan Details</h1>

        <p>Manage your programs and assign your members.</p>
      </section>

      <Formik
        validationSchema={formSchema}
        initialValues={initialValues}
        onSubmit={onSubmitHundler}
      >
        {({
          values, isSubmitting, setFieldValue,
        }) => (
          <FormikForm className="needs-validation" noValidate="">
            <section>
              <HorizontalTextField name="name" label="Plan Name" required />

              <p className="mb-4">
                Let your members take advantage of the resources
                available on our platform. With access to Assessments and Webinars, they can
                stay ahead of industry trends and sharpen their skills.
                They can even buy coaching programs for themselves to further develop
                their skills and knowledge.
              </p>

              <HorizontalSelectButtonsField
                name="sessions"
                label="How many sessions should be included in your plan?"
                collection={[
                  {
                    label: 'Assessment Only',
                    value: 0,
                    disable: plan.attributes.sessions !== 0,
                  },
                  {
                    label: '3',
                    value: 3,
                    disable: plan.attributes.sessions !== 3,
                  },
                  {
                    label: '6',
                    value: 6,
                    disable: plan.attributes.sessions !== 6,
                  },
                  {
                    label: '12',
                    value: 12,
                    disable: plan.attributes.sessions !== 12,
                  },
                ]}
                required
              />

              <Row>
                <Col md={6}>
                  <HorizontalNumberField
                    className="justify-content-center"
                    name="membersCovered"
                    label="Number of members covered"
                    required
                  />

                  <HorizontalDateField
                    name="effectiveDate"
                    label="Effective date"
                    required
                    onChange={(date) => {
                      const renewal = new Date(date);
                      renewal.setFullYear(renewal.getFullYear() + 1);
                      setFieldValue('renewalDate', formatDate(renewal));
                    }}
                  />

                  {!isFree && (
                  <HorizontalSelectField
                    name="paymentInformationId"
                    label="Payment Method"
                    placeholder={paymentMethods === null ? 'Loading...' : 'Select Payment Method'}
                    options={paymentMethods === null ? [] : paymentMethods.map((item) => ({
                      label: item.attributes.label,
                      value: item.id,
                    }))}
                    required
                  />
                  )}

                </Col>

                <Col md={6}>
                  {!isFree && (
                    <HorizontalSelectButtonsField
                      name="billingType"
                      label="Type of billing"
                      collection={[
                        {
                          label: 'Monthly',
                          value: 'monthly',
                        },
                        {
                          label: 'Annual',
                          value: 'annual',
                          disable: true,
                        },
                      ]}
                      required
                    />
                  )}

                  {plan && (
                    <>
                      <div className="my-3">Renewal date</div>
                      <div className="fs-3">
                        {values.renewalDate}
                        &nbsp;
                      </div>

                      <div key="pepm" className="d-flex justify-content-between mt-3">
                        <div>PEPM</div>
                        <div>
                          {isFree && <span className="fs-5">Free</span>}
                          {!isFree && (
                            <>
                              <span className="me-1 fs-5">
                                {mark}
                                {formatCurrency(plan.attributes.price_employee_month, '')}
                              </span>
                              <span className="fw-light">{abbr}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {!isFree && (
                        <small key="pepm-notice">
                          Minimum
                            {' '}
                            {mark}
                            {pricyByCurrency(plan.attributes.currency.title)}
                          /month
                        </small>
                      )}

                      {plan.attributes.monthly_custom_records.map((item) => (
                        <div key={item.id} className="d-flex justify-content-between mt-3">
                          <small>{item.name}</small>
                          <small>
                            <span className="me-1 fs-5">
                              {mark}
                              {formatCurrency(item.price, '')}
                            </span>
                            <span className="fw-light">{abbr}</span>
                          </small>
                        </div>
                      ))}

                      {plan.attributes.monthly_discount_enable && (
                      <div>
                        <small>{plan.attributes.monthly_discount_name}</small>
                        <small className="ms-1">
                          {plan.attributes.monthly_discount}
                          %
                        </small>
                      </div>
                      )}

                      {values.billingType === 'annual' && plan && plan.attributes.annual_discount !== '' && (
                      <div>
                        <small>Annual Discount</small>
                        <small className="ms-1">
                          {plan.attributes.annual_discount}
                          %
                        </small>
                      </div>
                      )}

                      <Total plan={plan} />

                      {plan.attributes.onetime_custom_records.length > 0 && (
                      <>
                        <div>One-Time Premium</div>
                        {plan && plan.attributes.onetime_custom_records.map((item) => (
                          <div key={item.id} className="d-flex justify-content-between mt-3">
                            <small>{item.name}</small>
                            <small>
                              <span className="me-1 fs-5">
                                {mark}
                                {formatCurrency(parseFloat(item.price) * (item.is_activation_fee
                                  && plan.attributes.activation_fee_type === 'member'
                                  ? parseFloat(values.membersCovered) : 1), '')}
                              </span>
                              <span className="fw-light">{abbr}</span>
                            </small>
                          </div>
                        ))}

                        {plan.attributes.onetime_discount !== '' && (
                        <div>
                          <small>{plan.attributes.onetime_discount_name}</small>
                          <small className="ms-1">
                            {plan.attributes.onetime_discount}
                            %
                          </small>
                        </div>
                        )}
                      </>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </section>

            <footer>
              <Link
                to={routes.managers.policy.index}
                className="btn btn-outline-primary"
              >
                Cancel
              </Link>

              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting && (
                  <LoadingPlaceholder placement="inline" text=" " />
                )}
                Continue
              </Button>
            </footer>
          </FormikForm>
        )}
      </Formik>
    </>
  );
}

PlanDetails.propTypes = {
  enterpriseId: PropTypes.number.isRequired,
};
